import { useMemo } from "react";
import { createForm } from "@formily/core";
import { createSchemaField } from "@formily/react";
import {
  ArrayCards,
  ArrayTable,
  Cascader,
  Checkbox,
  DatePicker,
  Editable,
  Form,
  FormCollapse,
  FormGrid,
  FormItem,
  FormLayout,
  FormTab,
  Input,
  NumberPicker,
  Password,
  PreviewText,
  Radio,
  Select,
  Space,
  Switch,
  TimePicker,
  Transfer,
  TreeSelect,
  Upload,
} from "@formily/antd-v5";
import { Card } from "antd";
import UserSelect from "../UserSelect";
import EntitySelect from "../EntitySelect";
import RichTextInput from "../RichTextInput";
import MarkdownView from "../markdown/MarkdownView";

/** A guidance block (C12): renders its markdown, holds no answer. */
const Markdown = ({ content, style }: any) => (
  <MarkdownView value={content} style={style} />
);

/** Section headings defined in the schema render as their `mode` tag. */
const Text = ({ content, mode, style }: any) => {
  if (mode && mode !== "normal" && typeof mode === "string") {
    const Tag = mode as keyof JSX.IntrinsicElements;
    return <Tag style={style}>{content}</Tag>;
  }
  return <span style={style}>{content}</span>;
};

const SchemaField = createSchemaField({
  components: {
    ArrayCards,
    ArrayTable,
    Card,
    Cascader,
    Checkbox,
    DatePicker,
    Editable,
    FormCollapse,
    FormGrid,
    FormItem,
    FormLayout,
    FormTab,
    Input,
    NumberPicker,
    Password,
    PreviewText,
    Radio,
    Select,
    Space,
    Switch,
    Text,
    TimePicker,
    Transfer,
    TreeSelect,
    Upload,
    UserSelect,
    EntitySelect,
    // The same two components the real form registers -- a preview missing one
    // renders that field as nothing, which reads as a broken drop rather than
    // an unregistered component.
    RichText: RichTextInput,
    Markdown,
  },
});

/**
 * The form as the person filling it in will see it.
 *
 * Rendered by `@formily/antd-v5` — the same renderer that draws this form on a
 * real permit — so the preview cannot drift from the real thing. It is
 * deliberately inert: no request id, no values fetched, nothing saved. A
 * designer preview that talked to the server would be a way to corrupt live
 * data by clicking around in a design tool.
 */
const SchemaPreview = ({
  schema,
  formProps,
}: {
  schema: any;
  formProps?: Record<string, any>;
}) => {
  // Rebuilt whenever the schema changes so the preview always reflects the
  // current draft rather than the state it opened with. `schema` is a
  // deliberate cache key, not a value the factory reads — hence the override.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const form = useMemo(() => createForm(), [schema]);

  return (
    <Form {...(formProps ?? {})} form={form} onAutoSubmit={() => {}}>
      <SchemaField schema={schema} />
    </Form>
  );
};

export default SchemaPreview;
